import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronRight, Menu, X, Sun, Moon, Home, Building2, Wrench, Info, Phone, GraduationCap } from "lucide-react";
import { useServices, useSolutions, useBranding, useSiteSettings } from "@/hooks/useCms";

const STATIC_MENUS = [
  { label: "Home", href: "/", items: null, icon: Home },
  { label: "Soluções", href: "/solucoes", dynamic: "solutions", icon: Building2 },
  { label: "Serviços", href: "/servicos", dynamic: "services", icon: Wrench },
  { label: "Academia", href: "/academia", items: null, icon: GraduationCap },
  {
    label: "Sobre nós",
    href: "/sobre",
    icon: Info,
    items: [
      { label: "O Grupo", href: "/sobre/grupo" },
      { label: "Certificações", href: "/sobre/certificacoes" },
      { label: "Notícias", href: "/sobre/noticias" },
      { label: "Oportunidades de Trabalho", href: "/sobre/oportunidades" },
      { label: "Blog", href: "/sobre/blog" },
    ],
  },
  { label: "Contactos", href: "/contactos", items: null, icon: Phone },
];

const Logo = () => {
  const { logoLight, logoDark } = useBranding();
  const location = useLocation();
  const handleClick = (e) => {
    // Se já estamos na Home, evitar navegação SPA "vazia": scroll ao topo + refresh
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo(0, 0);
      window.location.reload();
    }
  };
  return (
    <Link to="/" data-testid="nav-logo" onClick={handleClick} className="flex items-center shrink-0">
      <div className="relative h-8 w-[180px] shrink-0">
        <img
          src={logoLight}
          alt="INXPEK"
          className="absolute inset-0 h-full w-full object-contain object-left dark:opacity-0 transition-opacity"
        />
        <img
          src={logoDark}
          alt="INXPEK"
          className="absolute inset-0 h-full w-full object-contain object-left opacity-0 dark:opacity-100 transition-opacity"
        />
      </div>
    </Link>
  );
};

export default function Navbar() {
  const location = useLocation();
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("inexpek-theme") === "dark");
  const { data: services } = useServices();
  const { data: solutions } = useSolutions();
  const settings = useSiteSettings();
  const ctaLabel = settings?.nav_cta_label || "My Inxpek";
  const ctaUrl = (settings?.nav_cta_url || "").trim();
  // Limites de itens nos dropdowns (0 = mostrar todos)
  const solutionsLimit = Math.max(0, Number(settings?.nav_solutions_limit ?? 10) | 0);
  const servicesLimit = Math.max(0, Number(settings?.nav_services_limit ?? 10) | 0);

  const MENUS = useMemo(() => {
    // Aplica limite + acrescenta "Ver mais…" quando o total excede o limite.
    const buildItems = (arr, limit, href, hrefKey) => {
      const items = arr.map((s) => ({
        label: s.title,
        href: hrefKey === "hash" ? `/servicos#${s.slug}` : `/solucoes/${s.slug}`,
      }));
      if (limit > 0 && items.length > limit) {
        const clipped = items.slice(0, limit);
        clipped.push({ label: "Ver mais…", href, isMore: true });
        return clipped;
      }
      return items;
    };
    return STATIC_MENUS.map((m) => {
      if (m.dynamic === "solutions") {
        return { ...m, items: buildItems(solutions, solutionsLimit, "/solucoes", "slash") };
      }
      if (m.dynamic === "services") {
        return { ...m, items: buildItems(services, servicesLimit, "/servicos", "hash") };
      }
      return m;
    });
  }, [services, solutions, solutionsLimit, servicesLimit]);

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname === href || location.pathname.startsWith(href + "/");
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("inexpek-theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Fechar drawer mobile ao mudar de rota
  useEffect(() => {
    setMobile(false);
    setMobileExpanded(null);
  }, [location.pathname]);

  // Body scroll-lock + ESC para fechar
  useEffect(() => {
    if (!mobile) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setMobile(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [mobile]);

  const toggleMobileSection = (label) => {
    setMobileExpanded((cur) => (cur === label ? null : label));
  };

  return (
    <>
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 backdrop-blur-xl border-b ${
        scrolled
          ? "bg-white/95 border-slate-200/70 dark:bg-[#0A0E16]/95 dark:border-white/10 shadow-[0_1px_12px_rgba(15,23,42,0.06)]"
          : "bg-white dark:bg-[#0A0E16] border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1">
          {MENUS.map((m) => {
            const active = isActive(m.href);
            return (
            <div
              key={m.label}
              className="relative"
              onMouseEnter={() => setOpen(m.label)}
              onMouseLeave={() => setOpen(null)}
            >
              <Link
                to={m.href}
                data-testid={`nav-link-${m.label.toLowerCase().replace(/\s/g, "-")}`}
                aria-current={active ? "page" : undefined}
                className={`relative flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "text-[#1698AC] dark:text-[#4FC6D8]"
                    : "text-slate-700 dark:text-slate-300 hover:text-[#1698AC] dark:hover:text-[#4FC6D8]"
                }`}
              >
                {m.label}
                {m.items && <ChevronDown size={14} className="opacity-50" />}
                {active && (
                  <motion.span
                    layoutId="nav-active-underline"
                    className="absolute left-3 right-3 -bottom-0.5 h-[2px] rounded-full bg-[#1698AC] dark:bg-[#4FC6D8]"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
              <AnimatePresence>
                {m.items && open === m.label && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18 }}
                    className="absolute top-full left-0 pt-2 w-60"
                  >
                    <div className="bg-white dark:bg-[#101828] border border-slate-200 dark:border-white/10 rounded-md shadow-[0_12px_32px_rgba(0,0,0,0.08)] py-2">
                      {m.items.map((it) => (
                        <Link
                          key={it.label}
                          to={it.href}
                          onClick={() => setOpen(null)}
                          data-testid={`nav-sub-${it.label.toLowerCase().replace(/\s/g, "-").replace(/…/g, "more")}`}
                          className={
                            it.isMore
                              ? "flex items-center gap-1 px-5 py-2.5 mt-1 border-t border-slate-100 dark:border-white/10 text-sm font-semibold text-[#1698AC] dark:text-[#4FC6D8] hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                              : "block px-5 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:text-[#1698AC] dark:hover:text-[#4FC6D8] hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                          }
                        >
                          {it.label}
                          {it.isMore && <ChevronDown size={12} className="rotate-[-90deg]" />}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            );
          })}
        </nav>
        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={() => setDark(!dark)}
            aria-label={dark ? "Modo claro" : "Modo escuro"}
            className="w-10 h-10 rounded-full border border-slate-200 dark:border-white/15 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:border-[#1698AC] hover:text-[#1698AC] dark:hover:border-[#4FC6D8] dark:hover:text-[#4FC6D8] active:scale-95 transition-all"
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {ctaUrl ? (
            <a
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-proposta"
              className="hidden sm:inline-flex items-center h-10 px-6 rounded-full bg-[#1698AC] text-white text-sm font-medium hover:bg-[#0F7484] active:scale-95 transition-all"
            >
              {ctaLabel}
            </a>
          ) : (
            <button
              onClick={() => window.dispatchEvent(new Event("open-agendamento"))}
              data-testid="nav-cta-proposta"
              className="hidden sm:inline-flex items-center h-10 px-6 rounded-full bg-[#1698AC] text-white text-sm font-medium hover:bg-[#0F7484] active:scale-95 transition-all"
            >
              {ctaLabel}
            </button>
          )}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setMobile(!mobile)}
            className="lg:hidden p-2 text-slate-700 dark:text-slate-300"
            aria-label="Menu"
          >
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
    </header>
    <AnimatePresence>
        {mobile && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobile(false)}
              className="lg:hidden fixed inset-0 top-[72px] bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm z-40"
              data-testid="nav-mobile-backdrop"
              aria-hidden="true"
            />

            {/* Drawer painel deslizante da direita */}
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="lg:hidden fixed top-[72px] right-0 bottom-0 w-[88%] max-w-[380px] bg-white dark:bg-[#0A0E16] shadow-[-8px_0_32px_rgba(15,23,42,0.12)] flex flex-col z-50 border-l border-slate-100 dark:border-white/10"
              data-testid="nav-mobile-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Menu de navegação"
            >
              {/* Uppercase label + navegação */}
              <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-5">
                <p className="px-3 mb-3 text-[10px] font-semibold tracking-[0.18em] text-slate-400 dark:text-slate-500 uppercase">
                  Navegação
                </p>
                <nav className="space-y-1.5">
                  {MENUS.map((m, idx) => {
                    const active = isActive(m.href);
                    const hasItems = Array.isArray(m.items) && m.items.length > 0;
                    const isExpanded = mobileExpanded === m.label;
                    const Icon = m.icon;
                    return (
                      <motion.div
                        key={m.label}
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.04 * idx + 0.05, duration: 0.25 }}
                      >
                        <div
                          className={`flex items-stretch rounded-xl overflow-hidden border transition-colors ${
                            active
                              ? "border-[#1698AC]/30 bg-[#1698AC]/8 dark:border-[#4FC6D8]/30 dark:bg-[#4FC6D8]/10"
                              : "border-slate-100 dark:border-white/8 bg-slate-50/70 dark:bg-white/[0.03] hover:border-slate-200 dark:hover:border-white/15"
                          }`}
                        >
                          <Link
                            to={m.href}
                            onClick={() => setMobile(false)}
                            aria-current={active ? "page" : undefined}
                            data-testid={`nav-mobile-${m.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={`flex-1 flex items-center gap-3 min-h-[54px] px-4 text-[15px] font-medium transition-colors ${
                              active
                                ? "text-[#1698AC] dark:text-[#4FC6D8]"
                                : "text-slate-800 dark:text-slate-100"
                            }`}
                          >
                            {Icon && (
                              <span
                                className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                                  active
                                    ? "bg-[#1698AC] text-white dark:bg-[#4FC6D8] dark:text-[#0A0E16]"
                                    : "bg-white dark:bg-white/5 border border-slate-200/70 dark:border-white/10 text-slate-500 dark:text-slate-400"
                                }`}
                              >
                                <Icon size={16} strokeWidth={2.2} />
                              </span>
                            )}
                            <span className="flex-1">{m.label}</span>
                            {!hasItems && (
                              <ChevronRight
                                size={16}
                                className="opacity-40 group-hover:opacity-70"
                              />
                            )}
                          </Link>
                          {hasItems && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                toggleMobileSection(m.label);
                              }}
                              aria-expanded={isExpanded}
                              aria-controls={`mobile-sub-${m.label}`}
                              data-testid={`nav-mobile-toggle-${m.label.toLowerCase().replace(/\s/g, "-")}`}
                              className={`w-14 flex items-center justify-center border-l transition-colors ${
                                active
                                  ? "border-[#1698AC]/20 dark:border-[#4FC6D8]/20 text-[#1698AC] dark:text-[#4FC6D8]"
                                  : "border-slate-100 dark:border-white/8 text-slate-500 dark:text-slate-400 hover:text-[#1698AC] dark:hover:text-[#4FC6D8]"
                              }`}
                              aria-label={isExpanded ? `Fechar ${m.label}` : `Abrir ${m.label}`}
                            >
                              <motion.span
                                animate={{ rotate: isExpanded ? 90 : 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <ChevronRight size={18} />
                              </motion.span>
                            </button>
                          )}
                        </div>

                        {/* Sub-menu accordion */}
                        <AnimatePresence initial={false}>
                          {hasItems && isExpanded && (
                            <motion.div
                              id={`mobile-sub-${m.label}`}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                              className="overflow-hidden"
                            >
                              <div className="pl-14 pr-2 pt-2 pb-1 space-y-0.5">
                                {m.items.map((it, subIdx) => (
                                  <motion.div
                                    key={it.label}
                                    initial={{ opacity: 0, x: 8 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.03 * subIdx, duration: 0.2 }}
                                  >
                                    <Link
                                      to={it.href}
                                      onClick={() => setMobile(false)}
                                      data-testid={`nav-mobile-sub-${it.label.toLowerCase().replace(/\s/g, "-").replace(/…/g, "more")}`}
                                      className={
                                        it.isMore
                                          ? "flex items-center gap-1.5 py-2.5 px-3 rounded-lg text-sm font-semibold text-[#1698AC] dark:text-[#4FC6D8] hover:bg-[#1698AC]/8 dark:hover:bg-[#4FC6D8]/10 transition-colors"
                                          : "flex items-center gap-2 py-2.5 px-3 rounded-lg text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-[#1698AC] dark:hover:text-[#4FC6D8] transition-colors"
                                      }
                                    >
                                      <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600 shrink-0" />
                                      <span className="flex-1">{it.label}</span>
                                      {it.isMore && <ChevronRight size={12} />}
                                    </Link>
                                  </motion.div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </nav>

                {/* Preferências */}
                <p className="px-3 mt-6 mb-2 text-[10px] font-semibold tracking-[0.18em] text-slate-400 dark:text-slate-500 uppercase">
                  Preferências
                </p>
                <button
                  type="button"
                  onClick={() => setDark(!dark)}
                  data-testid="nav-mobile-theme-toggle"
                  className="w-full flex items-center gap-3 min-h-[54px] px-4 rounded-xl border border-slate-100 dark:border-white/8 bg-slate-50/70 dark:bg-white/[0.03] hover:border-slate-200 dark:hover:border-white/15 transition-colors"
                >
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-white dark:bg-white/5 border border-slate-200/70 dark:border-white/10 text-slate-500 dark:text-slate-400">
                    {dark ? <Sun size={16} strokeWidth={2.2} /> : <Moon size={16} strokeWidth={2.2} />}
                  </span>
                  <span className="flex-1 text-left text-[15px] font-medium text-slate-800 dark:text-slate-100">
                    {dark ? "Modo claro" : "Modo escuro"}
                  </span>
                  <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {dark ? "ON" : "OFF"}
                  </span>
                </button>
              </div>

              {/* CTA fixo no fundo */}
              <div className="border-t border-slate-100 dark:border-white/10 bg-white/95 dark:bg-[#0A0E16]/95 backdrop-blur px-4 py-4">
                {ctaUrl ? (
                  <a
                    href={ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobile(false)}
                    data-testid="nav-cta-mobile"
                    className="flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-gradient-to-r from-[#1698AC] to-[#0F7484] text-white text-sm font-semibold shadow-[0_10px_24px_rgba(22,152,172,0.35)] hover:shadow-[0_14px_32px_rgba(22,152,172,0.45)] active:scale-[0.98] transition-all"
                  >
                    {ctaLabel}
                    <ChevronRight size={16} />
                  </a>
                ) : (
                  <button
                    onClick={() => { setMobile(false); window.dispatchEvent(new Event("open-agendamento")); }}
                    data-testid="nav-cta-mobile"
                    className="w-full flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-gradient-to-r from-[#1698AC] to-[#0F7484] text-white text-sm font-semibold shadow-[0_10px_24px_rgba(22,152,172,0.35)] hover:shadow-[0_14px_32px_rgba(22,152,172,0.45)] active:scale-[0.98] transition-all"
                  >
                    {ctaLabel}
                    <ChevronRight size={16} />
                  </button>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
